import { WifiOffIcon } from "lucide-react";
import { useSyncExternalStore, type ReactNode } from "react";

import { cn } from "@/lib/utils";

import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "./ui/empty";

/* eslint-disable react-refresh/only-export-components -- the connectivity hook and its presentation share one shell contract */

function subscribeToConnectivity(onStoreChange: () => void) {
  window.addEventListener("online", onStoreChange);
  window.addEventListener("offline", onStoreChange);

  return () => {
    window.removeEventListener("online", onStoreChange);
    window.removeEventListener("offline", onStoreChange);
  };
}

function getOnlineSnapshot() {
  return navigator.onLine;
}

function getServerOnlineSnapshot() {
  return true;
}

export function useOnlineStatus() {
  return useSyncExternalStore(subscribeToConnectivity, getOnlineSnapshot, getServerOnlineSnapshot);
}

export function OfflineState({
  action,
  className,
  description = "CCBox tools need an internet connection. Reconnect to Wi-Fi or cellular data; CCBox will recover automatically.",
  title = "Internet connection required",
  variant = "panel",
}: {
  action?: ReactNode;
  className?: string;
  description?: ReactNode;
  title?: string;
  variant?: "banner" | "panel";
}) {
  if (variant === "banner") {
    return (
      <Alert
        aria-live="polite"
        className={cn("offline-banner", className)}
        data-offline-state
        role="status"
      >
        <WifiOffIcon aria-hidden="true" />
        <AlertTitle>{title}</AlertTitle>
        <AlertDescription>{description}</AlertDescription>
      </Alert>
    );
  }

  return (
    <Empty
      aria-live="polite"
      className={cn("offline-panel max-w-lg", className)}
      data-offline-state
      role="status"
    >
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <WifiOffIcon aria-hidden="true" />
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        <EmptyDescription>{description}</EmptyDescription>
      </EmptyHeader>
      {action ? <EmptyContent>{action}</EmptyContent> : null}
    </Empty>
  );
}
